/* eslint-disable react-refresh/only-export-components */
/**
 * Gallery scene registry for /?test=maater-ui.
 *
 * Status is data, not prose. Agents copy whatever scene they open, so a scene
 * (or part of one) that a newer scene replaced says so here and on the page.
 * Retired rows also live in the Retired table in
 * .cursor/skills/maater-design-ui/SKILL.md.
 */
import { AlertTriangle, Palette } from 'lucide-react'
import { testIds } from '@/lib/test-ids'

export type SceneId =
  | 'open-destinations'
  | 'list-gestures'
  | 'sidebar-tree'
  | 'index-chrome'
  | 'preview-drill'
  | 'entity-menu'
  | 'menu-chrome'
  | 'reader-chrome'
  | 'index-vs-entity-tabs'
  | 'wait-sync'
  | 'list-status-chrome'
  | 'extension-limb'
  | 'shell'
  | 'harness'
  | 'mode-panel-stack'
  | 'panel-collapse'
  | 'panel-motion'
  | 'tab-groups'
  | 'open-echo'
  | 'presence'
  | 'settings-chrome'
  | 'list-overflow'
  | 'create-in-folder'
  | 'person-view'
  | 'toasts'
  | 'composer-offers'
  | 'diff-review'
  | 'inbox-attention'

/**
 * current - the contract; copy it.
 * art - direction only; do not port as-is.
 * superseded - replaced by `supersededBy`; do not copy any of it.
 */
export type SceneStatus = 'current' | 'art' | 'superseded'

/** One part of a current scene that a newer scene replaced. */
export interface SceneStalePart {
  what: string
  supersededBy: SceneId
  since: string
}

export interface GalleryScene {
  id: SceneId
  label: string
  summary: string
  status: SceneStatus
  supersededBy?: SceneId
  supersededOn?: string
  staleParts?: SceneStalePart[]
}

export const SCENES: GalleryScene[] = [
  {
    id: 'open-destinations',
    label: 'Open destinations',
    summary: 'Locked icons for Open / new tab / Preview / agent / chat / external',
    status: 'current',
  },
  {
    id: 'list-gestures',
    label: 'List gestures',
    summary: 'Click select, Cmd/Ctrl+click new tab, double-click open, PanelRight',
    status: 'current',
    staleParts: [
      {
        what: 'The ".. Up one level" row in the demo list. The parent crumb is the way up.',
        supersededBy: 'sidebar-tree',
        since: '2026-10-29',
      },
    ],
  },
  {
    id: 'sidebar-tree',
    label: 'Sidebar file tree',
    summary: 'Folder click lists that folder. File click opens that file.',
    status: 'current',
  },
  {
    id: 'index-chrome',
    label: 'Index chrome',
    summary: 'Todos SoT: Cmd+F, footer (?). Search Activity != Basecamp Activity',
    status: 'current',
    staleParts: [
      {
        what: 'The filter row full width above the peek. Filters now sit in the list column on the peek line.',
        supersededBy: 'sidebar-tree',
        since: '2026-10-30',
      },
    ],
  },
  {
    id: 'preview-drill',
    label: 'Preview drill',
    summary: 'Peek follows the selected main row. Folder click in preview moves main.',
    status: 'current',
    staleParts: [
      {
        what: 'The frame and the labeled peek header (uppercase title row, "Folder Preview"). Copy the drill behavior only.',
        supersededBy: 'panel-collapse',
        since: '2026-11-01',
      },
    ],
  },
  {
    id: 'entity-menu',
    label: 'Entity menu',
    summary: 'Right-click and ... share one item source (ADR-0020)',
    status: 'current',
  },
  {
    id: 'menu-chrome',
    label: 'Menu chrome',
    summary: 'Quiet overflow shell, destructive hover, confirm dialog inset',
    status: 'current',
  },
  {
    id: 'reader-chrome',
    label: 'Reader chrome',
    summary: 'Inbox-shaped reader: single title row, soul timeline, decision event',
    status: 'current',
  },
  {
    id: 'index-vs-entity-tabs',
    label: 'Index vs entity tabs',
    summary: 'App group for indexes; workspace groups for entity tabs',
    status: 'current',
  },
  {
    id: 'wait-sync',
    label: 'Wait / Sync frames',
    summary: 'Every-frame craft - Syncing / Synced / Conflict',
    status: 'current',
  },
  {
    id: 'list-status-chrome',
    label: 'List status chrome',
    summary: 'Bottom status + (i) teach; view ... for refresh - not banners',
    status: 'current',
  },
  {
    id: 'extension-limb',
    label: 'Extension limb',
    summary: 'Shell from this gallery; limb Settings via maater-extension-ui',
    status: 'current',
  },
  {
    id: 'shell',
    label: 'App shell',
    summary: 'Proto chrome on production light/dark tokens',
    status: 'current',
    staleParts: [
      {
        what: 'ProtoIconRail and header panel toggles. The rail, column toggles, and auto-hide flyout live in #panel-collapse.',
        supersededBy: 'panel-collapse',
        since: '2026-10-31',
      },
    ],
  },
  {
    id: 'harness',
    label: 'Harness / frame',
    summary: 'Selected rows, tabs, TOC rail, Info dock - prod captures',
    status: 'current',
  },
  {
    id: 'mode-panel-stack',
    label: 'Mode panel stack',
    summary: 'Vercel-style drill-down - back + title, no expand trees',
    status: 'current',
  },
  {
    id: 'panel-collapse',
    label: 'Panel collapse',
    summary: 'Icon rail stays; PanelLeft and Bot toggles on the columns; auto-hide flyout',
    status: 'current',
  },
  {
    id: 'panel-motion',
    label: 'Panel motion',
    summary: 'Secondary dock enter styles - pick one; no width tween',
    status: 'current',
  },
  {
    id: 'tab-groups',
    label: 'Tab groups',
    summary: 'Only / collapse, plus pin and Cmd-click open rules',
    status: 'current',
  },
  {
    id: 'open-echo',
    label: 'Open echo',
    summary: 'Tree/extension open-dot; Open Tabs pin echo + shared tab context menu',
    status: 'current',
  },
  {
    id: 'presence',
    label: 'Who\'s here',
    summary: 'Placement E locked. Chip and tooltip still a high-level abstract.',
    status: 'art',
  },
  {
    id: 'settings-chrome',
    label: 'Settings chrome',
    summary: 'SettingsSection SoT - Basecamp muted box; Account / Keys / Secrets',
    status: 'current',
  },
  {
    id: 'list-overflow',
    label: 'List overflow',
    summary: 'Show N more / Browse show-hidden - Eye + italic; workspace MRU cap',
    status: 'current',
  },
  {
    id: 'create-in-folder',
    label: 'Create in folder',
    summary: '+ New - empty desk, folder submenu, tab-bar split',
    status: 'current',
  },
  {
    id: 'person-view',
    label: 'Person view',
    summary: 'Workspace Browse noun + org tab - not a rail mode or App-group People app',
    status: 'current',
  },
  {
    id: 'toasts',
    label: 'Toasts',
    summary: 'Quiet card, tone on icon, hover close + swipe, short durations',
    status: 'current',
  },
  {
    id: 'composer-offers',
    label: 'Composer offers',
    summary: 'Keyword offer is a text link. Accept replaces the word with the slash badge.',
    status: 'current',
  },
  {
    id: 'diff-review',
    label: 'Diff review',
    summary: 'In-document block diff is the default. Source compare stays opt-in.',
    status: 'current',
  },
  {
    id: 'inbox-attention',
    label: 'Inbox attention',
    summary: 'Shared vs only you, why chip, Inbox / Digest / Log, reply in thread',
    status: 'art',
  },
]

export function findScene(id: SceneId): GalleryScene | undefined {
  return SCENES.find((s) => s.id === id)
}

/** Banner at the top of a scene body. Renders nothing for a clean current scene. */
export function SceneStatusBanner({ scene }: { scene: GalleryScene | undefined }) {
  if (!scene) return null
  if (scene.status === 'superseded') {
    return (
      <div
        className="mb-3 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
        data-testid={testIds.maaterUiGallery.sceneStatusBanner}
        data-proto-status="superseded"
      >
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <p>
          Superseded by <code>#{scene.supersededBy}</code>
          {scene.supersededOn ? ` on ${scene.supersededOn}` : ''}. Do not copy this chrome.
        </p>
      </div>
    )
  }
  if (scene.status === 'art') {
    return (
      <div
        className="mb-3 flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
        data-testid={testIds.maaterUiGallery.sceneStatusBanner}
        data-proto-status="art"
      >
        <Palette className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <p>Art direction, not a contract. Do not port this scene as-is.</p>
      </div>
    )
  }
  if (!scene.staleParts?.length) return null
  return (
    <div
      className="mb-3 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
      data-testid={testIds.maaterUiGallery.sceneStatusBanner}
      data-proto-status="partly-superseded"
    >
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <ul className="space-y-1">
        {scene.staleParts.map((part) => (
          <li key={part.what}>
            Do not copy: {part.what} Superseded by <code>#{part.supersededBy}</code> on{' '}
            {part.since}.
          </li>
        ))}
      </ul>
    </div>
  )
}
